package com.arena_web.data.local

import com.arena_web.data.dto.ClubDto
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import org.w3c.fetch.Response

/**
 * Persisted copy of the full public club catalog (web mirror of the mobile
 * ClubsCatalogCache). The Arena map renders these clubs instantly on entry and
 * a background sync re-downloads the catalog to pick up rating/profile changes.
 *
 * Storage backend is the Cache API, NOT localStorage: the catalog is ~5M JSON
 * chars, which localStorage would hold as UTF-16 (~10 MB) — right at Chrome's
 * origin quota and double Safari's ~5 MB cap, where setItem threw
 * QuotaExceededError and (uncaught) froze the Arena page on iPhones. The Cache
 * API quota is in the gigabytes on both engines and it is available in every
 * secure context (prod HTTPS and localhost alike).
 */
object ClubsCatalogCache {
    private val json = Json { ignoreUnknownKeys = true }
    private val serializer = ListSerializer(ClubDto.serializer())

    private const val CACHE_NAME = "arena-data"
    // Request-shaped key the Cache API stores the JSON under; never fetched.
    private const val CACHE_KEY = "/__app-cache__/clubs-catalog.json"

    suspend fun load(): List<ClubDto>? = runCatching {
        // One-time migration: free the huge legacy localStorage entry — on
        // Chrome it alone nearly exhausted the origin's localStorage quota.
        runCatching { Storage.remove(StorageKeys.CLUBS_CATALOG_CACHE) }
        val cache = window.caches.open(CACHE_NAME).await()
        val match = cache.match(CACHE_KEY).await() ?: return@runCatching null
        val text = (match as Response).text().await()
        // A schema change between app versions must not crash the tab — an
        // unreadable cache just means a cold start.
        json.decodeFromString(serializer, text)
    }.getOrNull()

    suspend fun save(clubs: List<ClubDto>) {
        // Best effort — a failed write (quota, private mode, no Cache API)
        // must never propagate: it would cancel the Arena page's effect scope
        // and freeze the whole tab. No cache just means a cold start.
        runCatching {
            val cache = window.caches.open(CACHE_NAME).await()
            val body = json.encodeToString(serializer, clubs)
            cache.put(CACHE_KEY, Response(body)).await()
        }
    }
}
